import React, { Fragment, useState } from "react";
import { Link } from "react-router";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import MapOutlinedIcon from "@mui/icons-material/MapOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import { visuallyHidden } from "@mui/utils";
import { useTranslation } from "react-i18next";
import { AdminOrganization, AdminRegistrationCode } from "src/Types/auth.types";
import { DASHBOARD_PATH } from "src/constants/paths";
import { formatDate, keyStatus, KeyStatus } from "./format";

const STATUS_CHIP_COLOR: Record<KeyStatus, "success" | "default" | "warning"> = {
  active: "success",
  revoked: "default",
  expired: "warning",
};

const subHeadingSx = {
  fontSize: "0.75rem",
  fontWeight: 700,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: "text.secondary",
  mb: 1,
} as const;

interface CitiesTableProps {
  organizations: AdminOrganization[];
  scanningId: string | null;
  onRunScan: (organization: AdminOrganization) => void;
  onRevoke: (code: AdminRegistrationCode, organization: AdminOrganization) => void;
}

const CityDetails: React.FC<{
  organization: AdminOrganization;
  onRevoke: CitiesTableProps["onRevoke"];
}> = ({ organization, onRevoke }) => {
  const { t } = useTranslation();
  return (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
      gap: 3,
      py: 2,
    }}
  >
    <Box>
      <Typography sx={subHeadingSx}>{t("admin.cities.keysHeading")}</Typography>
      {organization.registrationCodes.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t("admin.cities.noKeys")}
        </Typography>
      ) : (
        <Table size="small" aria-label={t("admin.cities.keysTableLabel", { name: organization.name })}>
          <TableHead>
            <TableRow>
              <TableCell>{t("admin.columns.label")}</TableCell>
              <TableCell align="right">{t("admin.columns.uses")}</TableCell>
              <TableCell>{t("admin.columns.expires")}</TableCell>
              <TableCell>{t("admin.columns.status")}</TableCell>
              <TableCell align="right">
                <Box component="span" sx={visuallyHidden}>
                  {t("admin.columns.actions")}
                </Box>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {organization.registrationCodes.map((code) => {
              const status = keyStatus(code);
              return (
                <TableRow key={code.id}>
                  <TableCell>{code.label || t("admin.untitledKey")}</TableCell>
                  <TableCell align="right">{code.useCount}</TableCell>
                  <TableCell>{code.expiresAt ? formatDate(code.expiresAt) : t("admin.never")}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      variant="outlined"
                      label={t(`admin.keyStatus.${status}`)}
                      color={STATUS_CHIP_COLOR[status]}
                    />
                  </TableCell>
                  <TableCell align="right">
                    {status === "active" && (
                      <Button
                        size="small"
                        color="error"
                        onClick={() => onRevoke(code, organization)}
                        aria-label={t("admin.cities.revokeLabel", { label: code.label || t("admin.untitledKey") })}
                      >
                        {t("admin.cities.revoke")}
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </Box>
    <Box>
      <Typography sx={subHeadingSx}>{t("admin.cities.usersHeading")}</Typography>
      {organization.members.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t("admin.cities.noUsers")}
        </Typography>
      ) : (
        <Table size="small" aria-label={t("admin.cities.usersTableLabel", { name: organization.name })}>
          <TableHead>
            <TableRow>
              <TableCell>{t("admin.columns.name")}</TableCell>
              <TableCell>{t("admin.columns.email")}</TableCell>
              <TableCell>{t("admin.columns.keyUsed")}</TableCell>
              <TableCell>{t("admin.columns.registered")}</TableCell>
              <TableCell>{t("admin.columns.lastLogin")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {organization.members.map((member) => (
              <TableRow key={member.id}>
                <TableCell>{member.name || "—"}</TableCell>
                <TableCell sx={{ wordBreak: "break-all" }}>{member.email}</TableCell>
                <TableCell>
                  {member.registrationCode
                    ? member.registrationCode.label || t("admin.untitledKey")
                    : "—"}
                </TableCell>
                <TableCell>{formatDate(member.createdAt)}</TableCell>
                <TableCell>{formatDate(member.lastLoginAt)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Box>
  </Box>
  );
};

const CitiesTable: React.FC<CitiesTableProps> = ({
  organizations,
  scanningId,
  onRunScan,
  onRevoke,
}) => {
  const { t } = useTranslation();
  const [expandedId, setExpandedId] = useState<string | null>(null);

  if (organizations.length === 0) {
    return (
      <Box sx={{ py: 8, textAlign: "center" }}>
        <Typography variant="h6">{t("admin.cities.emptyTitle")}</Typography>
        <Typography color="text.secondary" mt={0.5}>
          {t("admin.cities.emptyBody")}
        </Typography>
      </Box>
    );
  }

  return (
    <TableContainer>
      <Table aria-label={t("admin.cities.tableLabel")}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: 48 }}>
              <Box component="span" sx={visuallyHidden}>
                {t("admin.cities.expand")}
              </Box>
            </TableCell>
            <TableCell>{t("admin.accounts.city")}</TableCell>
            <TableCell>{t("admin.columns.boundary")}</TableCell>
            <TableCell align="right">{t("admin.columns.users")}</TableCell>
            <TableCell align="right">{t("admin.columns.activeKeys")}</TableCell>
            <TableCell>{t("admin.columns.lastScan")}</TableCell>
            <TableCell align="right">{t("admin.columns.actions")}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {organizations.map((organization) => {
            const expanded = expandedId === organization.id;
            const activeKeys = organization.registrationCodes.filter(
              (code) => keyStatus(code) === "active"
            ).length;
            const scanning = scanningId === organization.id;
            return (
              <Fragment key={organization.id}>
                <TableRow
                  hover
                  sx={{ "& > td": { borderBottom: expanded ? "none" : undefined } }}
                >
                  <TableCell>
                    <IconButton
                      size="small"
                      aria-label={t(expanded ? "admin.cities.hideDetails" : "admin.cities.showDetails", { name: organization.name })}
                      aria-expanded={expanded}
                      onClick={() => setExpandedId(expanded ? null : organization.id)}
                    >
                      {expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>{organization.name}</TableCell>
                  <TableCell sx={{ color: "text.secondary", maxWidth: 280 }}>
                    <Typography variant="body2" noWrap title={organization.boundaryName || ""}>
                      {organization.boundaryName || t("admin.cities.notSet")}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">{organization.members.length}</TableCell>
                  <TableCell align="right">{activeKeys}</TableCell>
                  <TableCell>
                    {organization.latestScan ? (
                      <Stack spacing={0.25}>
                        <Typography variant="body2">
                          {formatDate(organization.latestScan.createdAt)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {t("admin.cities.scanSummary", {
                            status: t(`dashboard.scanStatus.${organization.latestScan.status}`),
                            count: organization.latestScan.listingCount,
                          })}
                        </Typography>
                      </Stack>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        {t("admin.never")}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Tooltip
                        title={
                          organization.configured
                            ? ""
                            : t("admin.cities.scanNeedsArea")
                        }
                      >
                        <span>
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={!organization.configured || scanning}
                            onClick={() => onRunScan(organization)}
                            startIcon={
                              scanning ? (
                                <CircularProgress size={14} color="inherit" />
                              ) : (
                                <RefreshIcon fontSize="small" />
                              )
                            }
                            aria-label={t("admin.cities.runScanLabel", { name: organization.name })}
                            sx={{ whiteSpace: "nowrap" }}
                          >
                            {t("admin.cities.runScan")}
                          </Button>
                        </span>
                      </Tooltip>
                      <Button
                        size="small"
                        component={Link}
                        to={`${DASHBOARD_PATH}?city=${encodeURIComponent(organization.workspaceKey)}`}
                        startIcon={<MapOutlinedIcon fontSize="small" />}
                        aria-label={t("admin.cities.openDashboardLabel", { name: organization.name })}
                        sx={{ whiteSpace: "nowrap" }}
                      >
                        {t("nav.dashboard")}
                      </Button>
                    </Stack>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell colSpan={7} sx={{ py: 0, ...(expanded ? {} : { borderBottom: "none" }) }}>
                    <Collapse in={expanded} timeout="auto" unmountOnExit>
                      <CityDetails organization={organization} onRevoke={onRevoke} />
                    </Collapse>
                  </TableCell>
                </TableRow>
              </Fragment>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default CitiesTable;
